import { useEffect, useState } from 'react'

/**
 * SpaceX-style animated line component
 * Can expand from center, left, or right
 */
const AnimatedLine = ({
  trigger = true,
  direction = 'center', // 'center' | 'left' | 'right'
  color = 'accent',
  thickness = 1,
  duration = 600,
  delay = 0,
  className = '',
  glow = true,
}) => {
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (trigger) {
      const timer = setTimeout(() => setExpanded(true), delay)
      return () => clearTimeout(timer)
    } else {
      setExpanded(false)
    }
  }, [trigger, delay])

  const colorClasses = {
    accent: 'bg-accent-500',
    white: 'bg-white',
    gradient: 'bg-gradient-to-r from-transparent via-accent-500 to-transparent',
  }

  const glowClasses = {
    accent: 'shadow-[0_0_10px_rgba(99,102,241,0.5)]',
    white: 'shadow-[0_0_10px_rgba(255,255,255,0.5)]',
    gradient: 'shadow-[0_0_10px_rgba(99,102,241,0.5)]',
  }

  const originClasses = {
    center: 'left-1/2 -translate-x-1/2',
    left: 'left-0',
    right: 'right-0',
  }

  return (
    <div className={`relative w-full ${className}`} style={{ height: `${thickness}px` }}>
      <div
        className={`
          absolute top-0
          ${originClasses[direction]}
          h-full
          ${colorClasses[color]}
          ${glow && expanded ? glowClasses[color] : ''}
          transition-all ease-out
        `}
        style={{
          width: expanded ? '100%' : '0%',
          transitionDuration: `${duration}ms`,
          transform: direction === 'center' ? `translateX(-50%)` : undefined,
        }}
      />
    </div>
  )
}

/**
 * Animated line that follows scroll position
 */
export const ScrollProgressLine = ({
  color = 'accent',
  thickness = 2,
  position = 'top', // 'top' | 'bottom'
}) => {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      const scrollProgress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0
      setProgress(scrollProgress)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const colorClasses = {
    accent: 'bg-accent-500',
    white: 'bg-white',
  }

  return (
    <div
      className={`
        fixed ${position === 'top' ? 'top-0' : 'bottom-0'} left-0
        z-50
        ${colorClasses[color]}
      `}
      style={{
        width: `${progress}%`,
        height: `${thickness}px`,
        transition: 'width 50ms linear',
      }}
    />
  )
}

/**
 * Container that adds animated line on hover
 */
export const LineHoverContainer = ({
  children,
  className = '',
  linePosition = 'bottom', // 'top' | 'bottom'
}) => {
  const [hovered, setHovered] = useState(false)

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
      <div className={`absolute ${linePosition === 'top' ? 'top-0' : 'bottom-0'} left-0 w-full`}>
        <AnimatedLine trigger={hovered} duration={300} />
      </div>
    </div>
  )
}

export default AnimatedLine
